import { useState, useEffect, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { Participant, UserResponse } from "@/hooks/useAvailabilityData";

// Fixed emoji set for reactions
const REACTION_EMOJIS = ["👍", "❤️", "😂", "🎉"];

interface GuestBookProps {
  participants: Participant[];
  email: string;
  existingResponse?: UserResponse | null;
  onSubmit: (data: {
    name: string;
    email: string;
    note?: string;
  }) => void;
  toggleReaction: (targetEmail: string, emoji: string, currentUserEmail: string) => Promise<void>;
  hasReacted: (participant: Participant, emoji: string, currentUserEmail: string) => boolean;
  getReactionCount: (participant: Participant, emoji: string) => number;
}

export function GuestBook({
  participants,
  email,
  existingResponse,
  onSubmit,
  toggleReaction,
  hasReacted,
  getReactionCount,
}: GuestBookProps) {
  const [note, setNote] = useState("");
  const [reactingEmojis, setReactingEmojis] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (existingResponse) {
      setNote(existingResponse.note || "");
    }
  }, [existingResponse]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit({
      name: existingResponse?.name || "",
      email,
      note: note.trim() || undefined,
    });
  };

  const participantsWithNotes = participants.filter((p) => p.note && p.note.trim().length > 0);

  const handleReactionClick = async (participant: Participant, emoji: string) => {
    const reactionKey = `${participant.email}-${emoji}`;

    // Prevent multiple simultaneous reactions
    if (reactingEmojis.has(reactionKey)) {
      return;
    }

    setReactingEmojis((prev) => new Set(prev).add(reactionKey));

    try {
      await toggleReaction(participant.email, emoji, email);
    } catch (err) {
      console.error("Failed to toggle reaction:", err);
    } finally {
      setReactingEmojis((prev) => {
        const next = new Set(prev);
        next.delete(reactionKey);
        return next;
      });
    }
  };

  return (
    <div className="planner-box guest-book">
      <h2 className="planner-title">📖 Guest Book</h2>

      <div className="planner-box" style={{ marginBottom: "2rem" }}>
        <h3 className="planner-subtitle">Add Your Guest Book Entry</h3>
        <p className="planner-text" style={{ fontSize: "0.9rem", fontStyle: "italic", color: "#666", marginBottom: "1rem" }}>
          ⚠️ Friendly reminder: This form is protected by ancient HTML magic and the power of friendship.
          Don't even think about trying to hack it—our server-side validation is watching! 👀✨
        </p>
        <form onSubmit={handleSubmit} className="planner-form">
          <div className="form-group">
            <Label htmlFor="note-guestbook" className="planner-label">
              Guest Book Note (Optional - shared with everyone):
            </Label>
            <Textarea
              id="note-guestbook"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="planner-textarea"
              placeholder="Leave a message for everyone..."
              rows={4}
            />
          </div>

          <Button type="submit" className="planner-button">
            {existingResponse ? "Update Entry →" : "Add Entry →"}
          </Button>
        </form>
      </div>

      {participantsWithNotes.length === 0 ? (
        <p className="planner-text">No messages yet. Be the first to leave a note!</p>
      ) : (
        <div className="guest-book-entries">
          {participantsWithNotes.map((participant, index) => {
            const reactionKey = `${participant.email}`;
            const isReacting = Array.from(reactingEmojis).some((key) => key.startsWith(reactionKey));

            return (
              <div key={`${participant.email}-${index}`} className="guest-book-entry">
                <div className="guest-book-header">
                  <span className="guest-book-name">{participant.name}</span>
                  <span className="guest-book-date">
                    {new Date(participant.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="guest-book-note">{participant.note}</div>
                <div className="guest-book-reactions" style={{ marginTop: "0.75rem", display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                  {REACTION_EMOJIS.map((emoji) => {
                    const count = getReactionCount(participant, emoji);
                    const reacted = hasReacted(participant, emoji, email);
                    const isDisabled = isReacting || participant.email === email;

                    return (
                      <button
                        key={emoji}
                        onClick={() => handleReactionClick(participant, emoji)}
                        disabled={isDisabled}
                        className="reaction-button"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.25rem",
                          padding: "0.25rem 0.5rem",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          backgroundColor: reacted ? "#e3f2fd" : "transparent",
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          opacity: isDisabled ? 0.6 : 1,
                          fontSize: "0.875rem",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          if (!isDisabled) {
                            e.currentTarget.style.backgroundColor = reacted ? "#bbdefb" : "#f5f5f5";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isDisabled) {
                            e.currentTarget.style.backgroundColor = reacted ? "#e3f2fd" : "transparent";
                          }
                        }}
                      >
                        <span>{emoji}</span>
                        {count > 0 && <span style={{ fontSize: "0.75rem", color: "#666" }}>{count}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

